import React, { useState } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { ReactComponent as DarkModeLogo } from 'assets/logos/symbot-icon-blue.svg';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import {
  ArrowOutward,
  Download,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useAppTheme from 'hooks/useAppTheme';
import { useChatbotContext } from '../context/ChatbotContext';
import {
  isYokohamaTheme,
  getClientLogoSmall,
  CLIENT_THEME,
  clientThemePalette,
} from 'utils/clientTheme';
import { useTranslation } from 'react-i18next';
import { type InspectionImage } from '../types/chatbot.response.types';
import { useAppSelector } from 'store/hook';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import TirePressureAPI from 'api/tire-pressure';

interface AIResponseBubbleProps {
  message: string | React.ReactNode;
  navigatePath?: string;
  loading?: boolean;
  image?: string;
  inspectionImages?: InspectionImage[];
  report?: {
    type: 'monthly_tire_performance' | 'tire_inflation';
    startDate?: string;
    endDate?: string;
  };
}

const COLLAPSED_ROWS = 5;

const MarkdownTable = ({
  node,
  accentColor,
  fontSize,
}: {
  node: any;
  accentColor: string;
  fontSize: string;
}) => {
  const [expanded, setExpanded] = useState(false);

  const thead = node.children?.find((c: any) => c.tagName === 'thead');
  const tbody = node.children?.find((c: any) => c.tagName === 'tbody');
  const headerRow = thead?.children?.find((c: any) => c.tagName === 'tr');
  const headerCells =
    headerRow?.children?.filter((c: any) => c.tagName === 'th') ?? [];
  const dataRows =
    tbody?.children?.filter((c: any) => c.tagName === 'tr') ?? [];
  const colCount = headerCells.length;

  const getCellText = (cell: any): string => {
    if (!cell) return '';
    return (cell.children ?? [])
      .map((c: any) => (c.type === 'text' ? c.value : getCellText(c)))
      .join('')
      .trim();
  };

  const visibleCols = Array.from({ length: colCount }, (_, i) => i).filter(
    (i) => {
      const tdCells = dataRows.map(
        (row: any) => row.children?.filter((c: any) => c.tagName === 'td')[i]
      );
      return tdCells.some((cell: any) => getCellText(cell) !== '');
    }
  );

  const hasMore = dataRows.length > COLLAPSED_ROWS;
  const visibleRows = expanded ? dataRows : dataRows.slice(0, COLLAPSED_ROWS);

  const thStyle: React.CSSProperties = {
    border: `1px solid ${accentColor}33`,
    padding: '0.5rem 0.75rem',
    textAlign: 'left',
    backgroundColor: `${accentColor}18`,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
  const tdStyle: React.CSSProperties = {
    border: `1px solid ${accentColor}22`,
    padding: '0.5rem 0.75rem',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ overflowX: 'auto', width: '100%', margin: '1rem 0' }}>
      <table
        style={{
          borderCollapse: 'collapse',
          minWidth: '100%',
          border: `1px solid ${accentColor}33`,
          fontSize,
        }}
      >
        <thead>
          <tr>
            {visibleCols.map((ci) => (
              <th key={ci} style={thStyle}>
                {getCellText(headerCells[ci])}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row: any, ri: number) => (
            <tr key={ri}>
              {visibleCols.map((ci) => (
                <td key={ci} style={tdStyle}>
                  {getCellText(
                    row.children?.filter((c: any) => c.tagName === 'td')[ci]
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {hasMore && (
        <button
          onClick={() => {
            setExpanded((v) => !v);
          }}
          style={{
            marginTop: '0.4rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: accentColor,
            fontSize: '0.75rem',
            padding: '2px 0',
          }}
        >
          {expanded
            ? `▲ Show less`
            : `▼ Show ${dataRows.length - COLLAPSED_ROWS} more rows`}
        </button>
      )}
    </div>
  );
};

const TypingDots = ({ accentColor }: { accentColor: string }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      py: 0.5,
      px: 0.5,
      '@keyframes dot-bounce': {
        '0%, 80%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
        '40%': { transform: 'translateY(-6px)', opacity: 1 },
      },
    }}
  >
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          backgroundColor: accentColor,
          animation: 'dot-bounce 1.2s ease-in-out infinite',
          animationDelay: `${i * 0.2}s`,
        }}
      />
    ))}
  </Box>
);

export const AIResponseBubble: React.FC<AIResponseBubbleProps> = ({
  message,
  navigatePath,
  loading,
  image,
  inspectionImages,
  report,
}) => {
  const { t } = useTranslation();
  const [activeIdx, setActiveIdx] = useState(0);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const customerCode = useAppSelector(
    (state) => state.authReducer.customer.code
  );
  const { enableDarkTheme } = useAppTheme();
  const { isExpanded } = useChatbotContext();
  const navigate = useNavigate();

  const colorMode = enableDarkTheme ? 'dark' : 'light';
  const themeColors = clientThemePalette[CLIENT_THEME][colorMode];
  const accentColor = themeColors.primary;
  const isYokohama = isYokohamaTheme();

  const tirePressureApi = new TirePressureAPI();

  const handleReportDownload = async () => {
    if (!report) return;
    setIsDownloadingReport(true);
    try {
      const api = tirePressureApi;
      let response;
      let filename;

      if (report.type === 'tire_inflation') {
        response = await api.downloadInflationReport(customerCode);
        filename = 'Tire_Inflation_Report.pdf';
      } else {
        response = await api.downloadReport(
          report.startDate ?? '',
          report.endDate ?? '',
          customerCode
        );
        const monthName = dayjs(report.startDate ?? '').format('MMMM_YYYY');
        filename = `Tire_Performance_Report_${monthName}.pdf`;
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      saveAs(blob, filename);
    } catch (error) {
      console.error('Failed to download report');
    } finally {
      setIsDownloadingReport(false);
    }
  };
  const clientLogo = getClientLogoSmall();

  const bubbleBg = enableDarkTheme ? `${themeColors.navbarBg}ee` : '#FFFFFF';
  const bubbleBorder = enableDarkTheme
    ? `${accentColor}33`
    : `${accentColor}44`;
  const textColor = enableDarkTheme ? '#FFFFFF' : '#26364C';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        gap: 1,
        flexShrink: 0,
      }}
    >
      {/* Avatar */}
      <Box
        sx={{
          flexShrink: 0,
          borderRadius: isYokohama ? '12px 12px 3px 12px' : '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '35px',
          height: '35px',
          background: isYokohama
            ? `linear-gradient(145deg, ${accentColor}dd, ${accentColor}99)`
            : '#1049E5',
          border: `1.5px solid ${accentColor}44`,
          boxShadow: `0 2px 8px ${accentColor}33`,
        }}
      >
        <Box
          sx={{
            width: '22px',
            height: '22px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {clientLogo.type === 'img' ? (
            <img
              src={clientLogo.src}
              alt={clientLogo.alt}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)',
              }}
            />
          ) : (
            <DarkModeLogo style={{ width: '100%', height: '100%' }} />
          )}
        </Box>
      </Box>

      {/* Bubble */}
      <Box
        sx={{
          maxWidth: '80%',
          minWidth: 0,
          overflow: 'hidden',
          py: loading ? 1.25 : 1.5,
          px: 2,
          borderRadius: '4px 20px 20px 20px',
          border: `1px solid ${bubbleBorder}`,
          backgroundColor: bubbleBg,
          color: textColor,
          boxShadow: enableDarkTheme
            ? `0 2px 8px rgba(0,0,0,0.3)`
            : '0 1px 4px rgba(0,0,0,0.06)',
          transition: 'all 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
          '& > *:first-child': { marginTop: 0 },
          '& > *:last-child': { marginBottom: 0 },
        }}
      >
        {loading ? (
          <TypingDots accentColor={accentColor} />
        ) : (
          <>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                strong: ({ node, ...props }) => (
                  <strong style={{ color: accentColor }} {...props} />
                ),
                a: ({ node, ...props }) => (
                  <a
                    style={{
                      color: accentColor,
                      textDecoration: 'underline',
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    {...props}
                  />
                ),
                p: ({ node, ...props }) => (
                  <Typography
                    component="p"
                    sx={{
                      fontSize: isExpanded ? '0.9rem' : '0.8rem',
                      margin: '0.5rem 0',
                      wordBreak: 'break-word',
                      lineHeight: 1.6,
                    }}
                    {...props}
                  />
                ),
                ul: ({ node, ...props }) => (
                  <ul
                    style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}
                    {...props}
                  />
                ),
                ol: ({ node, ...props }) => (
                  <ol
                    style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}
                    {...props}
                  />
                ),
                li: ({ node, ...props }) => (
                  <li
                    style={{
                      margin: '0.25rem 0',
                      fontSize: isExpanded ? '0.9rem' : '0.8rem',
                      lineHeight: 1.6,
                    }}
                    {...props}
                  />
                ),
                code: ({ node, inline, ...props }) => {
                  if (inline) {
                    return (
                      <code
                        style={{
                          backgroundColor: enableDarkTheme
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.05)',
                          padding: '0.2em 0.4em',
                          borderRadius: '3px',
                          fontFamily: 'monospace',
                          fontSize: '0.9em',
                        }}
                        {...props}
                      />
                    );
                  }
                  return (
                    <pre
                      style={{
                        backgroundColor: enableDarkTheme
                          ? 'rgba(255, 255, 255, 0.08)'
                          : 'rgba(0, 0, 0, 0.04)',
                        padding: '1em',
                        borderRadius: '6px',
                        overflowX: 'auto',
                        margin: '1rem 0',
                        border: `1px solid ${accentColor}22`,
                      }}
                    >
                      <code style={{ fontFamily: 'monospace' }} {...props} />
                    </pre>
                  );
                },
                table: ({ node }) => (
                  <MarkdownTable
                    node={node}
                    accentColor={accentColor}
                    fontSize={isExpanded ? '0.85rem' : '0.75rem'}
                  />
                ),
              }}
            >
              {typeof message === 'string' ? message : String(message)}
            </ReactMarkdown>

            {image && (
              <Box sx={{ mt: 1.5, width: '100%', position: 'relative' }}>
                <img
                  src={image}
                  alt="chart"
                  style={{
                    width: '100%',
                    borderRadius: '8px',
                    display: 'block',
                  }}
                />
                <Tooltip title={t('download')}>
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      color: 'white',
                      backgroundColor: 'rgba(0,0,0,0.4)',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.65)' },
                    }}
                    onClick={() => {
                      const a = document.createElement('a');
                      a.href = image;
                      a.download = 'chart.png';
                      a.click();
                    }}
                  >
                    <Download fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {report && (
              <Box
                sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}
              >
                <CustomLoadingButton
                  loading={isDownloadingReport}
                  size="small"
                  variant="text"
                  endIcon={<Download />}
                  onClick={handleReportDownload}
                  sx={{
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    color: accentColor,
                  }}
                >
                  {report.type === 'tire_inflation'
                    ? t('download_inflation_report')
                    : t('download_performance_report')}
                </CustomLoadingButton>
              </Box>
            )}

            {inspectionImages &&
              inspectionImages.length > 0 &&
              (() => {
                const img = inspectionImages[activeIdx];
                const conditionColor =
                  img.condition === 'GOOD'
                    ? '#4caf50'
                    : img.condition === 'REPAIRABLE'
                    ? '#ff9800'
                    : '#f44336';
                return (
                  <Box sx={{ mt: 1.5, width: '100%' }}>
                    <Box
                      sx={{
                        position: 'relative',
                        borderRadius: '8px',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={img.url}
                        alt={img.caption}
                        style={{
                          width: '100%',
                          display: 'block',
                          maxHeight: '220px',
                          objectFit: 'cover',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background:
                            'linear-gradient(transparent, rgba(0,0,0,0.72))',
                          px: 1.5,
                          py: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#fff',
                            fontSize: '0.72rem',
                            lineHeight: 1.3,
                          }}
                        >
                          {img.position} · {img.bumper_number}
                        </Typography>
                        <Typography
                          sx={{
                            color: 'rgba(255,255,255,0.75)',
                            fontSize: '0.68rem',
                          }}
                        >
                          {new Date(img.timestamp).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Chip
                        label={img.condition}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: conditionColor,
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          height: '20px',
                        }}
                      />
                      {inspectionImages.length > 1 && (
                        <>
                          <IconButton
                            size="small"
                            disabled={activeIdx === 0}
                            onClick={() => {
                              setActiveIdx((i) => i - 1);
                            }}
                            sx={{
                              position: 'absolute',
                              left: 4,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              backgroundColor: 'rgba(0,0,0,0.45)',
                              color: '#fff',
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.65)',
                              },
                              '&.Mui-disabled': { opacity: 0.3 },
                            }}
                          >
                            <ChevronLeft fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={activeIdx === inspectionImages.length - 1}
                            onClick={() => {
                              setActiveIdx((i) => i + 1);
                            }}
                            sx={{
                              position: 'absolute',
                              right: 4,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              backgroundColor: 'rgba(0,0,0,0.45)',
                              color: '#fff',
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.65)',
                              },
                              '&.Mui-disabled': { opacity: 0.3 },
                            }}
                          >
                            <ChevronRight fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        mt: 0.75,
                        flexWrap: 'wrap',
                      }}
                    >
                      {img.average_tread_depth != null && (
                        <Typography
                          sx={{
                            fontSize: '0.72rem',
                            color: enableDarkTheme
                              ? 'rgba(255,255,255,0.6)'
                              : 'rgba(0,0,0,0.5)',
                          }}
                        >
                          {t('tread')}{' '}
                          <strong style={{ color: textColor }}>
                            {img.average_tread_depth}mm
                          </strong>
                        </Typography>
                      )}
                      {img.measured_air_pressure != null && (
                        <Typography
                          sx={{
                            fontSize: '0.72rem',
                            color: enableDarkTheme
                              ? 'rgba(255,255,255,0.6)'
                              : 'rgba(0,0,0,0.5)',
                          }}
                        >
                          {t('pressure')}{' '}
                          <strong style={{ color: textColor }}>
                            {img.measured_air_pressure} bar
                          </strong>
                        </Typography>
                      )}
                      {img.damage_reported && img.damage_details && (
                        <Typography
                          sx={{ fontSize: '0.72rem', color: '#f44336' }}
                        >
                          {t('damage')} {img.damage_details}
                        </Typography>
                      )}
                    </Box>
                    {inspectionImages.length > 1 && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 0.5,
                          mt: 0.75,
                        }}
                      >
                        {inspectionImages.map((_, i) => (
                          <Box
                            key={i}
                            onClick={() => {
                              setActiveIdx(i);
                            }}
                            sx={{
                              width: i === activeIdx ? 16 : 6,
                              height: 6,
                              borderRadius: '3px',
                              cursor: 'pointer',
                              backgroundColor:
                                i === activeIdx
                                  ? accentColor
                                  : enableDarkTheme
                                  ? 'rgba(255,255,255,0.25)'
                                  : 'rgba(0,0,0,0.2)',
                              transition: 'all 0.2s',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })()}

            {navigatePath && (
              <Box>
                <CustomLoadingButton
                  variant="text"
                  size="small"
                  sx={{
                    p: '2px 6px',
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    minWidth: 'auto',
                    '.MuiButton-endIcon': { marginLeft: 0 },
                    borderRadius: '20px',
                    textDecoration: 'underline',
                    color: accentColor,
                  }}
                  endIcon={<ArrowOutward />}
                  onClick={() => {
                    navigate(navigatePath);
                  }}
                >
                  {t('view')}
                </CustomLoadingButton>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
